import { Alert, TabContainer, TabSection } from '@digitools/honest-react';
import DeductibleAndCoinsurance, { DeductibleAndCoinsuranceInfo, } from 'src/packages/dental/pages/DeductibleAndCoinsurance';
import CoveredServices, { CoveredServicesInfo } from 'src/packages/dental/pages/CoveredServices';
import Claims, { ClaimsInfo } from 'src/packages/dental/pages/Claims';
import useMediaQuery from 'src/hooks/useMediaQuery';
import useDentalDataContext from 'src/packages/dental/hooks/useDentalContext';
import React, { useEffect, useState } from 'react';
import DentalText from 'src/packages/dental/constants/DentalText';
import useTranslate from 'src/hooks/useTranslate';
import { CLAIMS, COVERED_SERVICES, DEDUCTIBLES_AND_COINSURANCE, getMemberEvent, tealiumFormat, } from 'src/packages/dental/providers/DentalDataProvider';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import { GetBenefitsAndFrequencies, GetClaims, GetDeductibles, GetWaitingPeriods, HIPAA_VIOLATION, } from 'src/packages/dental/api/DentalApiCalls';
import useAuthentication from 'src/hooks/useAuthentication';
import { TabsDisclaimerText } from 'src/packages/dental/Dental';
import { Language } from 'src/types/Language';
import TileTabs from 'src/packages/dental/components/TileTabs';
const TabContent = () => {
    const isTitleView = useMediaQuery('(max-width: 780px)');
    const { activeTab, setActiveTab, subscriber, currentMember, setCoveredServicesLoading, setCurrentMember, setDeductiblesLoading, setClaimsLoading, tealiumFunction, } = useDentalDataContext();
    const { t } = useTranslate();
    const { authHeader } = useAuthentication();
    const hasWaitingPeriodData = currentMember?.waitingPeriods || currentMember?.waitingPeriodsError;
    const hasBenefitsData = currentMember?.benefitsError || currentMember?.benefitsAndFrequencies;
    const needsDeductibleData = currentMember && !currentMember.deductiblesError && !currentMember.deductiblesAndCoinsurance;
    const needsClaimData = currentMember && !currentMember?.claims && !currentMember?.claimSummaryError && !currentMember?.hipaaViolation;
    const memberEvent = getMemberEvent(currentMember, subscriber);
    const [track, setTrack] = useState(false);
    const trackEventSuccessOrFail = (error, data) => {
        if (error && track) {
            tealiumFunction({
                memberEvent: memberEvent,
                eventName: tealiumFormat(error),
                eventAction: TEALIUM_EVENT.EVENT_ACTION.ERROR,
            });
        }
        else if (data && track) {
            tealiumFunction({
                eventAction: TEALIUM_EVENT.EVENT_ACTION.TAB,
                eventName: tealiumFormat(activeTab) || '',
                memberEvent: memberEvent,
            });
        }
        setTrack(false);
    };
    useEffect(() => {
        if (!currentMember) {
            return;
        }
        if (activeTab == CLAIMS) {
            if (needsClaimData) {
                setClaimsLoading(true);
                //remove hardcoded language when we support Spanish
                GetClaims(authHeader(), Language.ENGLISH, currentMember.memberType, currentMember.id)
                    .then((claimsResponse) => {
                    currentMember.claims = claimsResponse?.claims;
                    if (claimsResponse.graphQlError === 'Claims ' + HIPAA_VIOLATION) {
                        currentMember.hipaaViolation = true;
                    }
                    else {
                        currentMember.claimSummaryError = claimsResponse?.graphQlError;
                    }
                    setCurrentMember(currentMember);
                })
                    .finally(() => {
                    setClaimsLoading(false);
                    trackEventSuccessOrFail(currentMember?.claimSummaryError, currentMember.claims || currentMember.hipaaViolation);
                });
            }
            else {
                trackEventSuccessOrFail(currentMember?.claimSummaryError, currentMember.claims || currentMember.hipaaViolation);
            }
        }
        else if (activeTab == DEDUCTIBLES_AND_COINSURANCE) {
            if (needsDeductibleData) {
                setDeductiblesLoading(true);
                //remove hardcoded language when we support Spanish
                GetDeductibles(authHeader(), Language.ENGLISH, currentMember.memberType, currentMember.id)
                    .then((deductiblesResponse) => {
                    currentMember.deductiblesAndCoinsurance = deductiblesResponse?.deductiblesAndCoinsurance;
                    currentMember.deductiblesError = deductiblesResponse.graphQlError;
                    setCurrentMember(currentMember);
                })
                    .finally(() => {
                    setDeductiblesLoading(false);
                    trackEventSuccessOrFail(currentMember?.deductiblesError, currentMember.deductiblesAndCoinsurance);
                });
            }
            else {
                trackEventSuccessOrFail(currentMember?.deductiblesError, currentMember?.deductiblesAndCoinsurance);
            }
        }
        else {
            setCoveredServicesLoading(true);
            if (hasBenefitsData) {
                trackEventSuccessOrFail(currentMember?.benefitsError, (currentMember.benefitsAndFrequencies));
                setCoveredServicesLoading(false);
            }
            else {
                (async () => {
                    if (!hasWaitingPeriodData) {
                        //remove hardcoded language when we support Spanish
                        const waitingPeriodsResponse = await GetWaitingPeriods(authHeader(), Language.ENGLISH, currentMember.memberType, currentMember.id);
                        currentMember.waitingPeriods = waitingPeriodsResponse?.waitingPeriods;
                        currentMember.waitingPeriodsError = waitingPeriodsResponse.graphQlError;
                    }
                    if (!hasBenefitsData) {
                        //remove hardcoded language when we support Spanish
                        const benefitsResponse = await GetBenefitsAndFrequencies(authHeader(), Language.ENGLISH, currentMember.id);
                        currentMember.benefitsAndFrequencies = benefitsResponse?.benefitsAndFrequencies;
                        currentMember.benefitsError = benefitsResponse.graphQlError;
                    }
                })().finally(() => {
                    setCurrentMember(currentMember);
                    setCoveredServicesLoading(false);
                    trackEventSuccessOrFail(currentMember.benefitsError, currentMember.benefitsAndFrequencies);
                });
            }
        }
    }, [activeTab, currentMember?.id]);
    return (<>
      {!isTitleView && <TabsDisclaimerText />}
      {isTitleView ? (<>
          {<>
              <TileTabs {...{
                className: `${activeTab ? 'mt-3 pb-0 ' : 'mt-0 '} ${subscriber?.planOverview?.inNetworkOnlyClause ? 'mb-0' : 'mb-2'}}`,
            }} id={'dental-tab-list'} tileList={[
                    {
                        imageName: 'covered-services',
                        title: t(DentalText.coveredServicesText),
                        component: COVERED_SERVICES,
                        action: () => { setTrack(true); }
                    },
                    {
                        imageName: 'deductibles-and-coinsurance',
                        title: t(DentalText.deductiblesTitle),
                        component: DEDUCTIBLES_AND_COINSURANCE,
                        action: () => { setTrack(true); }
                    },
                    {
                        imageName: 'claims',
                        title: t(DentalText.claimsText),
                        component: CLAIMS,
                        action: () => { setTrack(true); }
                    },
                ]}/>
            </>}
          {activeTab === COVERED_SERVICES && <CoveredServices />}
          {activeTab === DEDUCTIBLES_AND_COINSURANCE && <DeductibleAndCoinsurance />}
          {activeTab === CLAIMS && <Claims />}
        </>) : (<div style={{
                textAlign: 'left',
            }} className='mb-1 mt-3' data-public={true}>
          <TabContainer name={'dental-insurance-tab-container'} className={'mt-0 pt-0'} initialActiveTab={(activeTab || COVERED_SERVICES) + '_tab'}>
            <TabSection className={'p-0 m-0'} label={t(DentalText.coveredServicesText)} name={`${COVERED_SERVICES}_tab`} onClick={() => {
                setActiveTab(COVERED_SERVICES);
                setTrack(true);
            }}>
              {subscriber?.planOverview?.inNetworkOnlyClause && (<Alert type={'info'} className={'mb-3'}>
                  {subscriber.planOverview.inNetworkOnlyClause}
                </Alert>)}
              <CoveredServicesInfo />
            </TabSection>
            <TabSection label={t(DentalText.deductibleText)} name={`${DEDUCTIBLES_AND_COINSURANCE}_tab`} aria-selected={true} onClick={() => {
                setActiveTab(DEDUCTIBLES_AND_COINSURANCE);
                setTrack(true);
            }}>
              {subscriber?.planOverview?.inNetworkOnlyClause && (<Alert type={'info'} className={'mb-3'}>
                  {subscriber.planOverview.inNetworkOnlyClause}
                </Alert>)}
              <DeductibleAndCoinsuranceInfo />
            </TabSection>
            <TabSection label={t(DentalText.claimsText)} name={`${CLAIMS}_tab`} onClick={() => {
                setActiveTab(CLAIMS);
                setTrack(true);
            }}>
              {subscriber?.planOverview?.inNetworkOnlyClause && (<Alert type={'info'} className={'mb-3'}>
                  {subscriber.planOverview.inNetworkOnlyClause}
                </Alert>)}
              <ClaimsInfo />
            </TabSection>
          </TabContainer>
        </div>)}
    </>);
};
export default TabContent;
